import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ChevronDown, ChevronRight, Check, Clock, Trash2, Plus, Edit2, X, Pencil, MapPin,
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { useFacility } from '../context/FacilityContext';
import { facilitiesAPI, sportsAPI } from '../services/api';
import { isSubscriptionService } from '../utils/serviceType';
import OnboardingRibbon from '../features/home/OnboardingRibbon';
import { ONBOARDING_STEPS, onboardingHref } from '../features/home/onboardingSteps';
import { InfoBanner } from '../components/ui';

// ─── helpers ─────────────────────────────────────────────────────────────────

const BOOKING_OPTIONS = [
  { value: 'slot', label: 'Slot-based (time slots)' },
  { value: 'subscription', label: 'Subscription (membership)' },
];

function groundLabel(grounds, groundId) {
  const g = grounds.find((gr) => gr.id === groundId || gr.id === Number(groundId));
  return g ? g.name : `Ground ${groundId}`;
}

// ─── ServiceRow: displays a saved service with edit / delete / timings ───────

function ServiceRow({ service, grounds, onEdit, onDelete, inOnboarding, navigate }) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-ga-sm border border-ga-border-subtle bg-ga-bg-surface px-3 py-2.5">
      <div className="min-w-0">
        <p className="text-sm font-medium text-ga-text-primary truncate">{service.name}</p>
        <p className="text-xs text-ga-text-muted mt-0.5">
          {groundLabel(grounds, service.ground_id)}
          {service.pricing_model && ` · ${service.pricing_model === 'subscription' ? 'Subscription' : 'Slot-based'}`}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          title={isSubscriptionService(service) ? 'Edit subscription pricing' : 'Edit timings & slots'}
          onClick={() => {
            const base = isSubscriptionService(service)
              ? '/dashboard/config-individual-service'
              : '/dashboard/slot-rule';
            const path = inOnboarding ? `${base}?onboarding=1&step=slots` : base;
            navigate(path, { state: { serviceData: service } });
          }}
          className="rounded p-1.5 text-ga-text-muted hover:bg-ga-bg-surface-subtle hover:text-ga-primary"
        >
          <Clock className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          title="Edit service"
          onClick={() => onEdit(service)}
          className="rounded p-1.5 text-ga-text-muted hover:bg-ga-bg-surface-subtle"
        >
          <Edit2 className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          title="Remove service"
          onClick={() => onDelete(service.id)}
          className="rounded p-1.5 text-ga-danger/70 hover:bg-ga-danger/10 hover:text-ga-danger"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

// ─── AddServiceRow: inline form to add a custom service inside a sport ───────

function AddServiceRow({ grounds, sportId, templates = [], onAdd, onCancel }) {
  const [form, setForm] = useState({
    ground_id: grounds[0]?.id || '',
    name: '',
    pricing_model: 'slot',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.ground_id) return;
    setSaving(true);
    await onAdd({ ...form, sport_id: sportId });
    setSaving(false);
  };

  const applyTemplate = (t) => {
    setForm((p) => ({ ...p, name: t.name }));
  };

  return (
    <form onSubmit={handleSubmit}
      className="rounded-ga-sm border border-dashed border-ga-border-subtle bg-ga-bg-surface-subtle/50 px-3 py-2.5 space-y-2">
      {templates.length > 0 && (
        <div>
          <p className="text-xs text-ga-text-muted mb-1">Quick-fill from template:</p>
          <div className="flex flex-wrap gap-1.5">
            {templates.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => applyTemplate(t)}
                className="rounded-ga-full border border-ga-border-subtle bg-ga-bg-surface px-2.5 py-0.5 text-xs text-ga-text-primary hover:border-ga-primary hover:text-ga-primary transition-colors"
              >
                {t.name}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-end gap-2">
      <div className="flex-1 min-w-[160px]">
        <label className="block text-xs text-ga-text-muted mb-0.5">Service name *</label>
        <input
          type="text"
          value={form.name}
          onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
          placeholder="e.g. Practice session"
          className="w-full rounded-ga-sm border border-ga-border-subtle bg-ga-bg-surface px-2 py-1.5 text-sm text-ga-text-primary"
          autoFocus
        />
      </div>
      <div className="min-w-[130px]">
        <label className="block text-xs text-ga-text-muted mb-0.5">Ground *</label>
        <select
          value={form.ground_id}
          onChange={(e) => setForm((p) => ({ ...p, ground_id: Number(e.target.value) }))}
          className="w-full rounded-ga-sm border border-ga-border-subtle bg-ga-bg-surface px-2 py-1.5 text-sm text-ga-text-primary"
        >
          {grounds.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
      </div>
      <div className="min-w-[150px]">
        <label className="block text-xs text-ga-text-muted mb-0.5">Booking type</label>
        <select
          value={form.pricing_model}
          onChange={(e) => setForm((p) => ({ ...p, pricing_model: e.target.value }))}
          className="w-full rounded-ga-sm border border-ga-border-subtle bg-ga-bg-surface px-2 py-1.5 text-sm text-ga-text-primary"
        >
          {BOOKING_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      <div className="flex gap-1.5">
        <button
          type="submit"
          disabled={saving || !form.name.trim()}
          className="rounded-ga-sm bg-ga-primary px-3 py-1.5 text-xs font-medium text-ga-text-on-primary hover:bg-ga-primary/90 disabled:opacity-50"
        >
          {saving ? 'Adding…' : 'Add'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-ga-sm border border-ga-border-subtle px-3 py-1.5 text-xs text-ga-text-muted hover:bg-ga-bg-surface-subtle"
        >
          Cancel
        </button>
      </div>
      </div>
    </form>
  );
}

// ─── EditServiceModal: inline edit form ──────────────────────────────────────

function EditServiceModal({ service, grounds, onSave, onCancel }) {
  const [form, setForm] = useState({
    name: service.name || '',
    ground_id: service.ground_id || '',
    pricing_model: service.pricing_model || 'slot',
    description: service.description || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave(service.id, form);
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit}
      className="mt-2 space-y-3 rounded-ga-sm border border-ga-primary/30 bg-ga-primary-soft p-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="block text-xs font-medium text-ga-text-muted mb-1">Name</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            className="w-full rounded-ga-sm border border-ga-border-subtle bg-ga-bg-surface px-2 py-1.5 text-sm text-ga-text-primary"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-ga-text-muted mb-1">Ground</label>
          <select
            value={form.ground_id}
            onChange={(e) => setForm((p) => ({ ...p, ground_id: Number(e.target.value) }))}
            className="w-full rounded-ga-sm border border-ga-border-subtle bg-ga-bg-surface px-2 py-1.5 text-sm text-ga-text-primary"
          >
            {grounds.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-ga-text-muted mb-1">Booking type</label>
          <select
            value={form.pricing_model}
            onChange={(e) => setForm((p) => ({ ...p, pricing_model: e.target.value }))}
            className="w-full rounded-ga-sm border border-ga-border-subtle bg-ga-bg-surface px-2 py-1.5 text-sm text-ga-text-primary"
          >
            {BOOKING_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-ga-text-muted mb-1">Description</label>
        <textarea
          value={form.description}
          onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
          rows={2}
          className="w-full rounded-ga-sm border border-ga-border-subtle bg-ga-bg-surface px-2 py-1.5 text-sm text-ga-text-primary"
        />
      </div>
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={saving}
          className="rounded-ga-sm bg-ga-primary px-3 py-1.5 text-xs font-medium text-ga-text-on-primary disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
        <button type="button" onClick={onCancel}
          className="rounded-ga-sm border border-ga-border-subtle px-3 py-1.5 text-xs text-ga-text-muted">
          Cancel
        </button>
      </div>
    </form>
  );
}

// ─── SportCard ────────────────────────────────────────────────────────────────

function SportCard({
  sport, grounds, existingServices, facilityId,
  onAddService, onUpdateService, onDeleteService,
  inOnboarding, navigate,
}) {
  const [expanded, setExpanded] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pendingChecks, setPendingChecks] = useState({}); // core_service_id → { ground_id, pricing_model }

  const sportServices = existingServices.filter(
    (s) => s.sport_id === sport.id || s.sport_slug === sport.slug,
  );

  const handleCheckboxToggle = async (cs) => {
    const already = sportServices.find((s) => s.core_service_id === cs.id);
    if (already) {
      await onDeleteService(already.id);
      return;
    }
    // Pre-select first ground; user can change in the ground picker that appears
    const defaultGroundId = grounds[0]?.id || null;
    setPendingChecks((p) => ({
      ...p,
      [cs.id]: p[cs.id] ? undefined : { ground_id: defaultGroundId, pricing_model: 'slot' },
    }));
  };

  const handleConfirmCheck = async (cs) => {
    const pending = pendingChecks[cs.id];
    if (!pending?.ground_id) return;
    setSaving(true);
    await onAddService({
      ground_id: pending.ground_id,
      core_service_id: cs.id,
      name: cs.name,
      type: cs.type || 'individual',
      category: pending.pricing_model === 'subscription' ? 'individual_sports' : 'team_sports',
      pricing_model: pending.pricing_model,
      sport_id: sport.id,
    });
    setPendingChecks((p) => { const n = { ...p }; delete n[cs.id]; return n; });
    setSaving(false);
  };

  const hasSavedServices = sportServices.length > 0;

  return (
    <div className={`rounded-ga-md border bg-ga-bg-surface shadow-ga-soft transition-colors ${
      hasSavedServices ? 'border-ga-primary/40' : 'border-ga-border-subtle'
    }`}>
      {/* Sport header */}
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
      >
        <div className="flex items-center gap-3">
          <span className="text-2xl leading-none" aria-hidden="true">{sport.icon}</span>
          <div>
            <p className="font-semibold text-ga-text-primary text-sm">{sport.name}</p>
            <p className="text-xs text-ga-text-muted mt-0.5">
              {hasSavedServices
                ? `${sportServices.length} service${sportServices.length !== 1 ? 's' : ''} configured`
                : 'No services yet'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasSavedServices && (
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-ga-primary text-ga-text-on-primary">
              <Check className="h-3 w-3" />
            </span>
          )}
          {expanded
            ? <ChevronDown className="h-4 w-4 text-ga-text-muted" />
            : <ChevronRight className="h-4 w-4 text-ga-text-muted" />}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-ga-border-subtle px-4 pb-4 pt-3 space-y-3">

          {/* Core service checklist */}
          {sport.core_services.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-ga-text-muted uppercase tracking-wide">
                Standard services — tick to add
              </p>
              {sport.core_services.map((cs) => {
                const saved = sportServices.find((s) => s.core_service_id === cs.id);
                const pending = pendingChecks[cs.id];
                return (
                  <div key={cs.id}>
                    <label className="flex items-start gap-2.5 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={!!saved}
                        onChange={() => handleCheckboxToggle(cs)}
                        className="mt-0.5 h-4 w-4 rounded border-ga-border-subtle text-ga-primary accent-ga-primary"
                      />
                      <span className="flex-1">
                        <span className="text-sm font-medium text-ga-text-primary">{cs.name}</span>
                        {cs.description && (
                          <span className="block text-xs text-ga-text-muted">{cs.description}</span>
                        )}
                      </span>
                    </label>

                    {/* Ground + booking picker for pending (just-checked) service */}
                    {pending && !saved && (
                      <div className="ml-6 mt-2 flex flex-wrap items-end gap-2 rounded-ga-sm border border-ga-primary/20 bg-ga-primary-soft p-2.5">
                        <div>
                          <label className="block text-xs text-ga-text-muted mb-0.5">Ground</label>
                          <select
                            value={pending.ground_id || ''}
                            onChange={(e) => setPendingChecks((p) => ({
                              ...p, [cs.id]: { ...p[cs.id], ground_id: Number(e.target.value) },
                            }))}
                            className="rounded-ga-sm border border-ga-border-subtle bg-ga-bg-surface px-2 py-1 text-xs text-ga-text-primary"
                          >
                            <option value="">Select ground</option>
                            {grounds.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-ga-text-muted mb-0.5">Booking type</label>
                          <select
                            value={pending.pricing_model}
                            onChange={(e) => setPendingChecks((p) => ({
                              ...p, [cs.id]: { ...p[cs.id], pricing_model: e.target.value },
                            }))}
                            className="rounded-ga-sm border border-ga-border-subtle bg-ga-bg-surface px-2 py-1 text-xs text-ga-text-primary"
                          >
                            {BOOKING_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                        </div>
                        <button
                          type="button"
                          disabled={!pending.ground_id || saving}
                          onClick={() => handleConfirmCheck(cs)}
                          className="rounded-ga-sm bg-ga-primary px-2.5 py-1 text-xs font-medium text-ga-text-on-primary disabled:opacity-50"
                        >
                          {saving ? 'Adding…' : 'Add →'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setPendingChecks((p) => { const n = { ...p }; delete n[cs.id]; return n; })}
                          className="rounded-ga-sm border border-ga-border-subtle px-2 py-1 text-xs text-ga-text-muted"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    )}

                    {/* Show saved service row */}
                    {saved && (
                      <div className="ml-6 mt-1.5">
                        {editingId === saved.id ? (
                          <EditServiceModal
                            service={saved}
                            grounds={grounds}
                            onSave={async (id, data) => {
                              await onUpdateService(id, data);
                              setEditingId(null);
                            }}
                            onCancel={() => setEditingId(null)}
                          />
                        ) : (
                          <ServiceRow
                            service={saved}
                            grounds={grounds}
                            onEdit={() => setEditingId(saved.id)}
                            onDelete={onDeleteService}
                            inOnboarding={inOnboarding}
                            navigate={navigate}
                          />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Custom services added to this sport */}
          {sportServices.filter((s) => !s.core_service_id).length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-ga-text-muted uppercase tracking-wide">
                Custom services
              </p>
              {sportServices.filter((s) => !s.core_service_id).map((s) => (
                editingId === s.id ? (
                  <EditServiceModal
                    key={s.id}
                    service={s}
                    grounds={grounds}
                    onSave={async (id, data) => { await onUpdateService(id, data); setEditingId(null); }}
                    onCancel={() => setEditingId(null)}
                  />
                ) : (
                  <ServiceRow
                    key={s.id}
                    service={s}
                    grounds={grounds}
                    onEdit={() => setEditingId(s.id)}
                    onDelete={onDeleteService}
                    inOnboarding={inOnboarding}
                    navigate={navigate}
                  />
                )
              ))}
            </div>
          )}

          {/* Add custom service for this sport */}
          {adding ? (
            <AddServiceRow
              grounds={grounds}
              sportId={sport.id}
              templates={sport.service_templates || []}
              onAdd={async (data) => { await onAddService(data); setAdding(false); }}
              onCancel={() => setAdding(false)}
            />
          ) : (
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="flex items-center gap-1.5 text-xs text-ga-primary hover:underline"
            >
              <Plus className="h-3.5 w-3.5" /> Add custom service
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── GroundsPanel ─────────────────────────────────────────────────────────────

function GroundsPanel({ grounds, onAdd, onRename, onDelete }) {
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [busy, setBusy] = useState(false);

  const submitAdd = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setBusy(true);
    const ok = await onAdd(newName.trim());
    if (ok) { setNewName(''); setAdding(false); }
    setBusy(false);
  };

  const startRename = (g) => { setEditingId(g.id); setEditName(g.name); };

  const submitRename = async (e) => {
    e.preventDefault();
    if (!editName.trim()) return;
    setBusy(true);
    const ok = await onRename(editingId, editName.trim());
    if (ok) setEditingId(null);
    setBusy(false);
  };

  return (
    <div className="mb-6 rounded-xl border border-ga-border bg-white dark:bg-gray-800 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-ga-border">
        <div>
          <h2 className="text-sm font-semibold text-ga-text-primary">Grounds / Courts / Arenas</h2>
          <p className="text-xs text-ga-text-muted mt-0.5">Each physical bookable space at your facility. Services are assigned to a ground.</p>
        </div>
        {!adding && (
          <button
            onClick={() => setAdding(true)}
            className="flex items-center gap-1.5 rounded-lg bg-ga-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-ga-primary/90"
          >
            <Plus className="h-3.5 w-3.5" /> Add ground
          </button>
        )}
      </div>

      <ul className="divide-y divide-ga-border">
        {grounds.map((g) => (
          <li key={g.id} className="flex items-center gap-3 px-4 py-2.5">
            {editingId === g.id ? (
              <form onSubmit={submitRename} className="flex flex-1 items-center gap-2">
                <input
                  autoFocus
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="flex-1 rounded border border-ga-primary bg-white px-2 py-1 text-sm dark:bg-gray-700"
                />
                <button type="submit" disabled={busy} className="rounded bg-ga-primary px-2.5 py-1 text-xs text-white disabled:opacity-50">Save</button>
                <button type="button" onClick={() => setEditingId(null)} className="text-xs text-ga-text-muted hover:text-ga-text-primary">Cancel</button>
              </form>
            ) : (
              <>
                <span className="flex-1 text-sm text-ga-text-primary font-medium">{g.name}</span>
                <button
                  onClick={() => startRename(g)}
                  className="rounded p-1 text-ga-text-muted hover:text-ga-primary hover:bg-gray-100 dark:hover:bg-gray-700"
                  title="Rename"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => onDelete(g.id)}
                  className="rounded p-1 text-ga-text-muted hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                  title="Delete"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </>
            )}
          </li>
        ))}
      </ul>

      {adding && (
        <form onSubmit={submitAdd} className="flex items-center gap-2 px-4 py-3 border-t border-dashed border-ga-border">
          <input
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="e.g. Cricket Ground 1, Football Turf, Court A…"
            className="flex-1 rounded border border-ga-border bg-white px-2 py-1.5 text-sm dark:bg-gray-700"
          />
          <button type="submit" disabled={busy || !newName.trim()} className="rounded bg-ga-primary px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50">
            {busy ? 'Adding…' : 'Add'}
          </button>
          <button type="button" onClick={() => { setAdding(false); setNewName(''); }} className="text-xs text-ga-text-muted hover:text-ga-text-primary">Cancel</button>
        </form>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function FacilityServices() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inOnboarding = searchParams.get('onboarding') === '1';

  const { activeFacilityId, allowedFacilities } = useFacility();
  const defaultFacilityId = activeFacilityId || (allowedFacilities[0]?.facility_id ?? null);
  const [facilityId, setFacilityId] = useState(defaultFacilityId);

  const [sports, setSports] = useState([]);
  const [grounds, setGrounds] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveMsg, setSaveMsg] = useState('');
  const saveMsgTimer = useRef(null);

  // Sync facility if global context changes
  useEffect(() => {
    if (activeFacilityId && activeFacilityId !== facilityId) {
      setFacilityId(activeFacilityId);
    }
  }, [activeFacilityId]); // eslint-disable-line

  const load = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    setError(null);
    try {
      const [sportsRes, groundsRes, servicesRes] = await Promise.all([
        sportsAPI.getAll(),
        facilitiesAPI.getGrounds(),
        facilitiesAPI.getServices(null, facilityId),
      ]);
      setSports((sportsRes?.data || []).sort((a, b) => a.display_order - b.display_order));
      const allGrounds = groundsRes?.data || [];
      setGrounds(allGrounds.filter((g) => g.facility_id === facilityId));
      const svcList = servicesRes?.success
        ? (servicesRes.data || [])
        : (Array.isArray(servicesRes) ? servicesRes : []);
      setServices(svcList);
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  useEffect(() => { load(); }, [load]);

  const showSaveMsg = (msg) => {
    setSaveMsg(msg);
    clearTimeout(saveMsgTimer.current);
    saveMsgTimer.current = setTimeout(() => setSaveMsg(''), 3000);
  };

  // Navigate to slot-rule for onboarding "Next" — always pass a service in state
  // so slot-rule doesn't redirect back here.
  const handleOnboardingNext = useCallback((nextStep) => {
    if (nextStep.id === 'slots') {
      const first = services[0];
      if (!first) {
        // No services yet — stay here and prompt
        setError('Add at least one service before configuring timings.');
        return;
      }
      navigate(`/dashboard/slot-rule?onboarding=1&step=slots`, { state: { serviceData: first } });
    } else {
      navigate(onboardingHref(nextStep));
    }
  }, [services, navigate]);

  const handleAddService = useCallback(async (data) => {
    const pricingModel = data.pricing_model || 'slot';
    // Custom services are individual/group bookings by default — only the full
    // cricket ground uses the team (two-sides) booking form. This keeps new
    // services off the team form unless explicitly typed as a cricket match.
    const serviceType = data.type || 'individual';
    const category = serviceType === 'cricket_match' ? 'team_sports' : 'individual_sports';
    try {
      const res = await facilitiesAPI.createService(facilityId, {
        ...data,
        category,
        pricing_model: pricingModel,
        type: serviceType,
      });
      if (res?.success) {
        showSaveMsg('Service added');
        await load();
      } else {
        setError(res?.message || 'Failed to add service');
      }
    } catch (e) {
      setError(e.message || 'Failed to add service');
    }
  }, [facilityId, load]);

  const handleUpdateService = useCallback(async (serviceId, data) => {
    const pricingModel = data.pricing_model || 'slot';
    const category = pricingModel === 'subscription' ? 'individual_sports' : 'team_sports';
    try {
      const res = await facilitiesAPI.updateService(serviceId, {
        ...data,
        category,
        pricing_model: pricingModel,
      });
      if (res?.success) {
        showSaveMsg('Service updated');
        await load();
      } else {
        setError(res?.message || 'Failed to update service');
      }
    } catch (e) {
      setError(e.message || 'Failed to update service');
    }
  }, [load]);

  const handleDeleteService = useCallback(async (serviceId) => {
    if (!window.confirm('Remove this service?')) return;
    try {
      const res = await facilitiesAPI.deleteService(serviceId);
      if (res?.success) {
        showSaveMsg('Service removed');
        await load();
      }
    } catch (e) {
      setError(e.message || 'Failed to delete service');
    }
  }, [load]);

  const handleAddGround = useCallback(async (name) => {
    try {
      const res = await facilitiesAPI.addGround(name);
      if (res?.success) { showSaveMsg('Ground added'); await load(); return true; }
      setError(res?.message || 'Failed to add ground');
    } catch (e) { setError(e.message || 'Failed to add ground'); }
    return false;
  }, [load]);

  const handleRenameGround = useCallback(async (groundId, name) => {
    try {
      const res = await facilitiesAPI.renameGround(groundId, name);
      if (res?.success) { showSaveMsg('Ground renamed'); await load(); return true; }
      setError(res?.message || 'Failed to rename ground');
    } catch (e) { setError(e.message || 'Failed to rename ground'); }
    return false;
  }, [load]);

  const handleDeleteGround = useCallback(async (groundId) => {
    if (!window.confirm('Delete this ground? This cannot be undone.')) return;
    try {
      const res = await facilitiesAPI.deleteGround(groundId);
      if (res?.success) { showSaveMsg('Ground deleted'); await load(); }
      else setError(res?.message || 'Failed to delete ground');
    } catch (e) { setError(e.message || 'Failed to delete ground'); }
  }, [load]);

  const facilityGrounds = grounds.filter((g) => g.facility_id === facilityId || !g.facility_id);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-24 text-ga-text-muted text-sm">Loading…</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-3xl px-4 py-6">
        {inOnboarding && <OnboardingRibbon onNext={handleOnboardingNext} />}

        {inOnboarding && searchParams.get('step') === 'slots' && (
          <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 flex items-start gap-2">
            <Clock className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              Expand a sport below and click the <strong>clock icon</strong> on a service to set its opening hours, slot duration and prices.
            </span>
          </div>
        )}

        <div className="mb-6">
          <h1 className="text-2xl font-bold text-ga-text-primary">Sports &amp; Services</h1>
          <p className="mt-1 text-sm text-ga-text-muted">
            Select the sports your facility offers, then add services within each sport.
            Each service is booked independently and needs its own ground and slot configuration.
          </p>
        </div>

        {allowedFacilities.length > 1 && (
          <div className="mb-5">
            <label className="block text-sm font-medium text-ga-text-primary mb-1">Facility</label>
            <select
              value={facilityId || ''}
              onChange={(e) => setFacilityId(Number(e.target.value) || null)}
              className="rounded-lg border border-ga-border bg-white px-3 py-2 text-sm dark:bg-gray-800"
            >
              {allowedFacilities.map((f) => (
                <option key={f.facility_id} value={f.facility_id}>
                  {f.facility_name || `Facility ${f.facility_id}`}
                </option>
              ))}
            </select>
          </div>
        )}

        <GroundsPanel
          grounds={facilityGrounds}
          onAdd={handleAddGround}
          onRename={handleRenameGround}
          onDelete={handleDeleteGround}
        />

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
            <button type="button" onClick={() => setError(null)} className="ml-2 underline text-xs">Dismiss</button>
          </div>
        )}

        {saveMsg && (
          <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            ✓ {saveMsg}
          </div>
        )}

        {/* Sport cards */}
        <div className="space-y-3">
          {sports.map((sport) => (
            <SportCard
              key={sport.id}
              sport={sport}
              grounds={facilityGrounds}
              existingServices={services}
              facilityId={facilityId}
              onAddService={handleAddService}
              onUpdateService={handleUpdateService}
              onDeleteService={handleDeleteService}
              inOnboarding={inOnboarding}
              navigate={navigate}
            />
          ))}
        </div>

        {services.length > 0 && (
          <div className="mt-6 rounded-xl border border-ga-border bg-white p-4 dark:bg-gray-800">
            <h2 className="text-sm font-semibold text-ga-text-primary mb-3">All configured services</h2>
            <ul className="divide-y divide-ga-border text-sm">
              {services.map((s) => (
                <li key={s.id} className="flex items-center justify-between gap-3 py-2">
                  <span className="font-medium text-ga-text-primary">{s.sport_icon} {s.name}</span>
                  <span className="text-xs text-ga-text-muted">
                    {groundLabel(facilityGrounds, s.ground_id)} · {s.pricing_model === 'subscription' ? 'Subscription' : 'Slot-based'}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
